// src/services/clubService.ts

import apiClient from "./apiClient";
import { ClubModel, CreateClubRequest, UpdateClubRequest } from "../models/ClubModel";
import { PageResponse } from "../models/PaginationModel";
import { CountryCountModel } from "../models/CountryCountModel";

const BASE_URL = "/api/clubs";

/**
 * Club Service - CRUD for the Club (parent organisation) resource.
 */
export const clubService = {
  /**
   * Get all active clubs with pagination.
   * Pass a 2-letter country code to restrict the list to a single country (left-pane filter).
   */
  async getAllClubs(
    page = 0,
    size = 20,
    sortBy = "shortName",
    sortDir = "asc",
    country?: string | null,
    // Viewer preference. Forwarded to the API so the badge counts (and the
    // "most active" ordering) exclude rookie-authored entries when the viewer
    // has them hidden — matches getAllTopics/getAllMatches so the "Bugünkü
    // Girişler" mechanism is identical across every left-pane domain.
    showRookieEntries = true
  ): Promise<PageResponse<ClubModel>> {
    const params = new URLSearchParams({
      page:    String(page),
      size:    String(size),
      sortBy,
      sortDir,
      showRookieEntries: String(showRookieEntries),
    });
    if (country) params.set("country", country);
    return apiClient.get<PageResponse<ClubModel>>(`${BASE_URL}?${params.toString()}`);
  },

  /**
   * Distinct country codes of active clubs with per-country counts —
   * feeds the left-pane country filter dropdown.
   */
  async getClubCountries(): Promise<CountryCountModel[]> {
    return apiClient.get<CountryCountModel[]>(`${BASE_URL}/countries`);
  },

  /**
   * Get club by database ID.
   */
  async getClubById(id: number): Promise<ClubModel> {
    return apiClient.get<ClubModel>(`${BASE_URL}/${id}`);
  },

  /**
   * Get club by SEO-friendly slug.
   */
  async getClubBySlug(slug: string): Promise<ClubModel> {
    return apiClient.get<ClubModel>(`${BASE_URL}/slug/${slug}`);
  },

  /**
   * Get clubs filtered by 2-letter country code.
   */
  async getClubsByCountry(country: string): Promise<ClubModel[]> {
    return apiClient.get<ClubModel[]>(`${BASE_URL}/country/${country}`);
  },

  /**
   * Search clubs by name or abbreviation.
   */
  async searchClubs(query: string): Promise<ClubModel[]> {
    return apiClient.get<ClubModel[]>(`${BASE_URL}/search?q=${encodeURIComponent(query)}`);
  },

  /**
   * Create a new club (Admin only).
   */
  async createClub(request: CreateClubRequest): Promise<ClubModel> {
    return apiClient.post<ClubModel>(BASE_URL, request);
  },

  /**
   * Update an existing club (Admin only).
   */
  async updateClub(id: number, request: UpdateClubRequest): Promise<ClubModel> {
    return apiClient.put<ClubModel>(`${BASE_URL}/${id}`, request);
  },

  /**
   * Soft-delete a club (Admin only).
   */
  async deleteClub(id: number): Promise<void> {
    await apiClient.delete<void>(`${BASE_URL}/${id}`);
  },
};

export default clubService;